// deps
import { useCallback, useContext, useMemo } from "react";

// internal deps
import { AnalyticsContext, isToingFlow } from "@swiggy-private/checkout-core";
import { format } from "@swiggy-private/core-ui";
import { OrderData } from "@swiggy-private/payment-types";
import {
    TOING_QUICK_SLA_OPTION,
    TOING_QUICK_TRACK_LOTTIE_URL,
    TOING_TRANSITION_LOTTIE_URL,
    TRANSITION_ANIMATION_SEGMENT,
    TRANSITION_LOADING_SEGMENT,
    TRANSITION_ANIMATION_SEGMENT_TOING_QUICK,
    TRANSITION_LOADING_SEGMENT_TOING_QUICK,
    TRANSITION_SUCCESS_SEGMENT_START_TOING,
    TRANSITION_SUCCESS_SEGMENT_START_TOING_QUICK,
    TRANSITION_LOTTIE_DELAY,
    TransitionLottieConfig,
} from "@swiggy-private/payment-ui";

// analytics
import {
    onImpressionAffordableCartM2PaymentConfirmation,
    onImpressionBoltPaymentConfirmation,
    onImpressionToingQuickPaymentConfirmation,
    paymentConfirmScreenV5BlackOTPViewEvent,
    paymentConfirmScreenV5ViewEvent,
} from "@payment-client/analytics/events/Payment";

import { TOING_QUICK_SAVING_TEXT } from "@payment-client/constants/TextDict";
import { getSavingsCalloutText } from "@payment-client/helpers/TickScreenTransitionHelper";

interface UseTickScreenTransitionInfoParams {
    orderData: OrderData;
}

interface UseTickScreenTransitionInfoReturn {
    transitionLottieConfig?: TransitionLottieConfig;
    onTransitionScreenView: () => void;
}

export const useTickScreenTransitionInfo = ({
    orderData,
}: UseTickScreenTransitionInfoParams): UseTickScreenTransitionInfoReturn => {
    const { impressionEvent } = useContext(AnalyticsContext);

    const { isToingQuick, isBoltCartM3, isAffordableCartM2, isOTPAnimationApplicable } = useMemo(
        () => ({
            isToingQuick: isToingFlow() && orderData?.selectedSlaOption === TOING_QUICK_SLA_OPTION,
            isBoltCartM3: orderData?.isBoltExperienceEnabledOnOrderConfirmationScreen ?? false,
            isAffordableCartM2: orderData?.isAffordableM2ExperienceEnabledOnOrderConfirmationScreen ?? false,
            isOTPAnimationApplicable: Boolean(
                orderData?.isSwiggyBlackOtpApplicable && orderData?.isSwiggyBlackOtpAnimationEnabled,
            ),
        }),
        [orderData],
    );

    const onTransitionScreenView = useCallback(() => {
        if (isBoltCartM3) {
            onImpressionBoltPaymentConfirmation(
                impressionEvent,
                JSON.stringify({
                    isBolt: isBoltCartM3,
                    isBlack: orderData?.isSwiggyBlackApplicable,
                    isBlackWithOtg: isOTPAnimationApplicable,
                }),
            );
        } else if (orderData?.isSwiggyBlackApplicable && isOTPAnimationApplicable) {
            paymentConfirmScreenV5BlackOTPViewEvent(impressionEvent);
        } else if (isAffordableCartM2) {
            onImpressionAffordableCartM2PaymentConfirmation(
                impressionEvent,
                JSON.stringify({
                    is99Store: isAffordableCartM2,
                    orderId: orderData?.orderId,
                }),
            );
        } else if (isToingQuick) {
            onImpressionToingQuickPaymentConfirmation(
                impressionEvent,
                String(orderData?.orderId ?? ""),
                format(TOING_QUICK_SAVING_TEXT, orderData?.quickModeSlaDiff ?? ""),
            );
        } else {
            paymentConfirmScreenV5ViewEvent(impressionEvent);
        }
    }, [
        orderData?.isSwiggyBlackApplicable,
        isOTPAnimationApplicable,
        isBoltCartM3,
        impressionEvent,
        isAffordableCartM2,
        isToingQuick,
        orderData?.orderId,
        orderData?.quickModeSlaDiff,
    ]);

    const transitionLottieConfig = useMemo((): TransitionLottieConfig | undefined => {
        const savingsText = getSavingsCalloutText(orderData?.successMessageInfoV2);
        if (isToingQuick) {
            return {
                url: TOING_QUICK_TRACK_LOTTIE_URL,
                layerText: {
                    savingscallout: savingsText,
                    timesavedcallout: orderData?.quickModeSlaDiff ?? "",
                },
                animationSegment: TRANSITION_ANIMATION_SEGMENT_TOING_QUICK,
                loadingSegment: TRANSITION_LOADING_SEGMENT_TOING_QUICK,
                delay: TRANSITION_LOTTIE_DELAY,
                successSegment: TRANSITION_SUCCESS_SEGMENT_START_TOING_QUICK,
            };
        } else if (isToingFlow()) {
            return {
                url: TOING_TRANSITION_LOTTIE_URL,
                layerText: {
                    savingscallout: savingsText,
                },
                animationSegment: TRANSITION_ANIMATION_SEGMENT,
                loadingSegment: TRANSITION_LOADING_SEGMENT,
                successSegment: TRANSITION_SUCCESS_SEGMENT_START_TOING,
            };
        }

        return undefined;
    }, [isToingQuick, orderData?.quickModeSlaDiff, orderData?.successMessageInfoV2]);

    return {
        transitionLottieConfig,
        onTransitionScreenView,
    };
};

export default useTickScreenTransitionInfo;
